/**
 * Testimonials Carousel - Luxury carousel with centre-focus effect.
 * Smooth slide transitions, auto-play with pause-on-hover, swipe support, infinite loop.
 */
class TestimonialsCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.swipeThreshold = 50;
    
    // Bind methods to preserve 'this' context
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
    this.handleSwipe = this.handleSwipe.bind(this);
  }

  connectedCallback() {
    // Initialize immediately - connectedCallback ensures DOM is ready
    this.initialize();
  }

  initialize() {
    // Prevent double initialization
    if (this.hasAttribute('data-initialized')) {
      return;
    }
    this.setAttribute('data-initialized', 'true');

    this.slides = Array.from(this.querySelectorAll('[data-testimonial-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-testimonial-dot]'));
    this.prevBtn = this.querySelector('[data-testimonial-prev]');
    this.nextBtn = this.querySelector('[data-testimonial-next]');
    this.track = this.querySelector('[data-testimonial-track]');
    
    const autoplayValue = this.dataset.autoplay || '6000';
    this.autoplaySpeed = parseInt(autoplayValue, 10) || 6000;

    if (!this.slides || this.slides.length < 1) {
      console.warn('TestimonialsCarousel: No slides found');
      return;
    }

    this.bindEvents();
    this.updateSlides();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.prev();
      });
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.next();
      });
    }

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goTo(i);
      });
    });

    /* Pause on hover — also pause progress animation */
    this.addEventListener('mouseenter', () => {
      this.stopAutoplay();
      this.setAttribute('data-paused', '');
    });
    
    this.addEventListener('mouseleave', () => {
      this.removeAttribute('data-paused');
      this.startAutoplay();
      this.restartProgress();
    });

    /* Touch / swipe support */
    this.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });

    /* Pause when not visible */
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.startAutoplay();
          } else {
            this.stopAutoplay();
          }
        });
      }, { threshold: 0.3 });
      this.observer.observe(this);
    }

    /* Keyboard navigation */
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
  }

  handleSwipe() {
    const diff = this.touchStartX - this.touchEndX;
    if (Math.abs(diff) < this.swipeThreshold) return;
    if (diff > 0) {
      this.next();
    } else {
      this.prev();
    }
  }

  prev() {
    if (this.isTransitioning) return;
    const newIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goTo(newIndex);
  }

  next() {
    if (this.isTransitioning) return;
    const newIndex = (this.currentIndex + 1) % this.slides.length;
    this.goTo(newIndex);
  }

  goTo(index) {
    if (this.isTransitioning || index === this.currentIndex) return;
    this.isTransitioning = true;
    this.currentIndex = index;
    this.updateSlides();
    this.resetAutoplay();

    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
  }

  updateSlides() {
    const total = this.slides.length;

    this.slides.forEach((slide, i) => {
      slide.removeAttribute('data-position');
      slide.setAttribute('aria-hidden', 'true');

      if (i === this.currentIndex) {
        slide.setAttribute('data-position', 'centre');
        slide.setAttribute('aria-hidden', 'false');
      } else if (i === (this.currentIndex - 1 + total) % total) {
        slide.setAttribute('data-position', 'left');
      } else if (i === (this.currentIndex + 1) % total) {
        slide.setAttribute('data-position', 'right');
      } else {
        slide.setAttribute('data-position', 'hidden');
      }
    });

    /* Update dots and restart progress animation */
    this.dots.forEach((dot, i) => {
      const isActive = i === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');

      /* Reset progress bar animation */
      const progress = dot.querySelector('.testimonials-carousel__dot-progress');
      if (progress) {
        progress.style.animation = 'none';
        /* Force reflow so the browser restarts the animation */
        void progress.offsetWidth;
        progress.style.animation = '';
      }
    });

    /* Update aria-live region */
    const liveRegion = this.querySelector('[data-testimonial-live]');
    if (liveRegion) {
      liveRegion.textContent = `Testimonial ${this.currentIndex + 1} of ${total}`;
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.slides || this.slides.length <= 1) return;
    this.autoplayTimer = setInterval(() => {
      if (!this.isTransitioning) {
        this.next();
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  restartProgress() {
    const activeDot = this.dots[this.currentIndex];
    if (!activeDot) return;
    const progress = activeDot.querySelector('.testimonials-carousel__dot-progress');
    if (progress) {
      progress.style.animation = 'none';
      void progress.offsetWidth;
      progress.style.animation = '';
    }
  }
}

// Register the custom element
if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}

